/*
  OnboardingReviewTab — clinician-facing view for Phase B.

  Shows every canonical field the client's intake submissions have
  projected into the registry, grouped by top-level namespace
  (demographics.*, meds.*, falls.*, goals.*, …). Each row lets the
  clinician:
    • Verify — mark the value as clinically confirmed.
    • Amend  — edit the value in place. The original patient wording
               is always preserved in history.
    • Decline — flag as inaccurate (with a reason).
    • Defer  — mark as "will confirm at appointment" (with a note).

  Uncatered pending items are shown first in an amber card at the
  top so the operator can burn through the worklist quickly.
*/
import { fmtIfDate } from "@/lib/intl";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Check, Pencil, X, Clock, ClipboardList, AlertTriangle, ChevronRight, ShieldCheck, Loader2,
} from "lucide-react";

const STATUS_CHIPS = {
  imported_unreviewed: {
    label: "Needs review", cls: "border-amber-300 bg-amber-50 text-amber-900",
    icon: AlertTriangle,
  },
  verified: {
    label: "Verified", cls: "border-emerald-300 bg-emerald-50 text-emerald-900",
    icon: Check,
  },
  amended: {
    label: "Amended", cls: "border-sky-300 bg-sky-50 text-sky-900",
    icon: Pencil,
  },
  declined: {
    label: "Declined", cls: "border-rose-300 bg-rose-50 text-rose-900",
    icon: X,
  },
  deferred: {
    label: "Deferred", cls: "border-zinc-300 bg-zinc-50 text-zinc-800",
    icon: Clock,
  },
};

function StatusChip({ status, auto = false }) {
  const cfg = STATUS_CHIPS[status] || STATUS_CHIPS.imported_unreviewed;
  const Icon = cfg.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded border ${cfg.cls}`}
      data-testid={`canonical-status-${status}`}
    >
      <Icon className="h-2.5 w-2.5" />
      {auto && status === "verified" ? "Verified · client-submitted" : cfg.label}
    </span>
  );
}

function displayValue(v) {
  if (v === null || v === undefined) return "—";
  if (typeof v === "string") return fmtIfDate(v) || "—";
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

function groupOf(key) {
  return key.includes(".") ? key.split(".")[0] : "misc";
}

export default function OnboardingReviewTab({ email }) {
  const [rows, setRows] = useState([]);
  const [counters, setCounters] = useState(null);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState(null); // { row, action } for the modal
  const [bulkPreview, setBulkPreview] = useState(null); // preview payload for confirm dialog
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkIncludeSafety, setBulkIncludeSafety] = useState(false);
  const [bulkNote, setBulkNote] = useState("");

  const load = useCallback(async () => {
    if (!email) return;
    setLoading(true);
    try {
      const [rowsRes, cntRes] = await Promise.all([
        api.get(`/canonical/${encodeURIComponent(email)}`),
        api.get(`/canonical/${encodeURIComponent(email)}/counters`),
      ]);
      setRows(rowsRes.data || []);
      setCounters(cntRes.data || null);
    } catch (e) {
      toast.error("Failed to load onboarding review", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setLoading(false);
    }
  }, [email]);

  useEffect(() => { load(); }, [load]);

  async function openBulkVerify() {
    try {
      const r = await api.get(`/canonical/${encodeURIComponent(email)}/verify-pending/preview`);
      setBulkPreview(r.data);
      setBulkIncludeSafety(false);
      setBulkNote("");
    } catch (e) {
      toast.error("Could not load bulk-verify preview", {
        description: e?.response?.data?.detail || e.message,
      });
    }
  }

  async function confirmBulkVerify() {
    setBulkBusy(true);
    try {
      const r = await api.post(`/canonical/${encodeURIComponent(email)}/verify-pending`, {
        note: bulkNote || undefined,
        include_safety_critical: bulkIncludeSafety,
      });
      const verified = r.data?.verified?.length || 0;
      const skipped = r.data?.skipped_safety_critical?.length || 0;
      if (verified === 0 && skipped === 0) {
        toast.info("Nothing to verify — all fields have already been reviewed.");
      } else {
        toast.success(`Verified ${verified} field${verified === 1 ? "" : "s"}`, {
          description: skipped
            ? `${skipped} safety-critical field${skipped === 1 ? "" : "s"} skipped — review individually below.`
            : "Everything on this client is now clinically confirmed.",
          duration: 6000,
        });
      }
      setBulkPreview(null);
      await load();
    } catch (e) {
      toast.error("Bulk verify failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally { setBulkBusy(false); }
  }

  const grouped = useMemo(() => {
    const g = new Map();
    for (const r of rows) {
      const key = groupOf(r.canonical_key);
      if (!g.has(key)) g.set(key, []);
      g.get(key).push(r);
    }
    return [...g.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [rows]);

  const pending = rows.filter((r) => r.status === "imported_unreviewed");

  if (loading) {
    return (
      <div className="text-sm text-zinc-500 p-8 text-center"
           data-testid="onboarding-review-loading">
        Loading onboarding review…
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="rounded-md border border-zinc-200 p-8 text-center"
           data-testid="onboarding-review-empty">
        <ClipboardList className="h-6 w-6 mx-auto mb-2 text-zinc-400" />
        <p className="text-sm text-zinc-600 font-medium">Nothing to review yet.</p>
        <p className="text-xs text-zinc-500 mt-1">
          Once this client submits an onboarding form, any field tagged with
          a <code>canonicalFieldKey</code> will land here as{" "}
          <span className="font-medium">Needs review</span>.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="onboarding-review-tab">
      {counters && (
        <div className="flex items-center gap-2 flex-wrap text-xs">
          <span className="font-medium text-zinc-700">
            {counters.total} canonical fields:
          </span>
          {["imported_unreviewed", "verified", "amended", "declined", "deferred"].map((k) => (
            counters[k] > 0 && (
              <span
                key={k}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-zinc-200 bg-white"
                data-testid={`onboarding-counter-${k}`}
              >
                <StatusChip status={k} />
                <span className="tabular-nums font-medium">{counters[k]}</span>
              </span>
            )
          ))}
        </div>
      )}

      {pending.length > 0 && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3"
             data-testid="onboarding-review-pending-card">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-amber-900 font-medium text-sm">
              <AlertTriangle className="h-4 w-4" />
              {pending.length} field{pending.length === 1 ? "" : "s"} awaiting your review
            </div>
            <Button
              size="sm"
              onClick={openBulkVerify}
              className="bg-emerald-700 hover:bg-emerald-800 text-white"
              data-testid="canonical-bulk-verify-open"
            >
              <ShieldCheck className="h-4 w-4 mr-1.5" />
              Verify all pending
            </Button>
          </div>
          <p className="text-xs text-amber-800 mt-2">
            The client's onboarding answers below have not been verified yet.
            Verify, amend, decline or defer each item — the original patient
            wording is always preserved in history. "Verify all pending" skips
            safety-critical fields (meds, allergies, falls history) by default.
          </p>
        </div>
      )}

      {grouped.map(([group, groupRows]) => (
        <div key={group}
             className="rounded-md border border-zinc-200 bg-white overflow-hidden"
             data-testid={`onboarding-group-${group}`}>
          <div className="px-3 py-2 bg-zinc-50 border-b border-zinc-200 text-xs font-semibold uppercase tracking-wider text-zinc-700">
            {group}
            <span className="ml-2 text-zinc-500 font-normal normal-case tracking-normal">
              · {groupRows.length} field{groupRows.length === 1 ? "" : "s"}
            </span>
          </div>
          <div className="divide-y divide-zinc-100">
            {groupRows.map((r) => (
              <ReviewRow key={r.id} row={r}
                          onAct={(kind) => setAction({ row: r, kind })} />
            ))}
          </div>
        </div>
      ))}

      {action && (
        <ActionDialog
          row={action.row}
          kind={action.kind}
          onClose={() => setAction(null)}
          onDone={() => { setAction(null); load(); }}
          email={email}
        />
      )}

      {bulkPreview && (
        <Dialog open={true} onOpenChange={(v) => { if (!v) setBulkPreview(null); }}>
          <DialogContent
            className="max-w-lg max-h-[90vh] flex flex-col p-0 gap-0"
            data-testid="canonical-bulk-verify-dialog"
          >
            <DialogHeader className="px-6 pt-6 pb-3 border-b border-zinc-200 shrink-0">
              <DialogTitle>Verify {bulkIncludeSafety ? bulkPreview.total_pending : bulkPreview.will_verify} pending field{(bulkIncludeSafety ? bulkPreview.total_pending : bulkPreview.will_verify) === 1 ? "" : "s"}?</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 px-6 py-4 overflow-y-auto flex-1 min-h-0 text-sm">
              <p className="text-zinc-700">
                Each field gets its own history entry with your name + optional note,
                exactly like verifying one-by-one. The original patient wording is
                preserved forever.
              </p>
              {bulkPreview.will_verify > 0 && (
                <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3">
                  <div className="text-xs font-semibold text-emerald-900 uppercase tracking-wider">
                    Safe to verify ({bulkPreview.will_verify})
                  </div>
                  <ul className="mt-1.5 text-xs text-emerald-900 space-y-0.5">
                    {(bulkPreview.safe_sample || []).map((s) => (
                      <li key={s.canonical_key} className="truncate">
                        · <code className="text-2xs">{s.canonical_key}</code>
                        {s.value !== null && s.value !== "" && (
                          <span className="text-emerald-800/80 ml-1">— {String(fmtIfDate(s.value)).slice(0, 60)}</span>
                        )}
                      </li>
                    ))}
                    {bulkPreview.will_verify > (bulkPreview.safe_sample?.length || 0) && (
                      <li className="text-2xs text-emerald-800/70">
                        …and {bulkPreview.will_verify - (bulkPreview.safe_sample?.length || 0)} more
                      </li>
                    )}
                  </ul>
                </div>
              )}
              {bulkPreview.safety_critical_skipped > 0 && (
                <div className="rounded-md border border-rose-200 bg-rose-50 p-3">
                  <div className="text-xs font-semibold text-rose-900 uppercase tracking-wider flex items-center gap-1.5">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    Safety-critical ({bulkPreview.safety_critical_skipped})
                  </div>
                  <ul className="mt-1.5 text-xs text-rose-900 space-y-0.5">
                    {(bulkPreview.safety_critical_sample || []).map((s) => (
                      <li key={s.canonical_key} className="truncate">
                        · <code className="text-2xs">{s.canonical_key}</code>
                      </li>
                    ))}
                  </ul>
                  <label className="mt-2.5 flex items-start gap-2 text-xs text-rose-900 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={bulkIncludeSafety}
                      onChange={(e) => setBulkIncludeSafety(e.target.checked)}
                      className="mt-0.5"
                      data-testid="canonical-bulk-verify-include-safety"
                    />
                    <span>
                      Also verify these safety-critical fields in bulk. I've reviewed
                      each one and confirm the client's answers are correct.
                    </span>
                  </label>
                </div>
              )}
              <div>
                <label className="text-xs font-medium text-zinc-700">Optional note</label>
                <Textarea
                  rows={2}
                  value={bulkNote}
                  onChange={(e) => setBulkNote(e.target.value)}
                  placeholder="e.g. Reviewed with client at intake appointment"
                  className="mt-1"
                  data-testid="canonical-bulk-verify-note"
                />
                <p className="text-2xs text-zinc-500 mt-1">
                  Attached to every row's audit-log entry. Leave blank to use the default
                  "Verified in bulk during onboarding review".
                </p>
              </div>
            </div>
            <DialogFooter className="px-6 py-3 border-t border-zinc-200 bg-white shrink-0">
              <Button variant="outline" onClick={() => setBulkPreview(null)} disabled={bulkBusy}>
                Cancel
              </Button>
              <Button
                onClick={confirmBulkVerify}
                disabled={bulkBusy || (bulkPreview.will_verify === 0 && !bulkIncludeSafety)}
                className="bg-emerald-700 hover:bg-emerald-800 text-white"
                data-testid="canonical-bulk-verify-confirm"
              >
                {bulkBusy && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
                Verify {bulkIncludeSafety ? bulkPreview.total_pending : bulkPreview.will_verify} field{(bulkIncludeSafety ? bulkPreview.total_pending : bulkPreview.will_verify) === 1 ? "" : "s"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}


function ReviewRow({ row, onAct }) {
  const [showHistory, setShowHistory] = useState(false);
  const originalEntry = (row.history || []).find((h) => h.action === "imported");
  const original = originalEntry?.original_value;
  const hasBeenChanged = row.status === "amended" &&
    JSON.stringify(original) !== JSON.stringify(row.value);

  return (
    <div className="px-3 py-2.5 hover:bg-zinc-50" data-testid={`canonical-row-${row.canonical_key}`}>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <code className="text-2xs font-mono text-zinc-600">{row.canonical_key}</code>
            <StatusChip status={row.status} auto={!!row.auto_verified} />
          </div>
          <div className="mt-1 text-sm text-zinc-900 whitespace-pre-wrap break-words">
            {displayValue(row.value)}
          </div>
          {hasBeenChanged && (
            <div className="mt-1 text-2xs text-zinc-500 italic">
              Amended from patient's original: <span className="font-mono">{displayValue(original)}</span>
            </div>
          )}
          <button
            type="button"
            onClick={() => setShowHistory((v) => !v)}
            className="mt-1 text-2xs text-indigo-700 hover:underline inline-flex items-center gap-0.5"
            data-testid={`canonical-history-toggle-${row.canonical_key}`}
          >
            <ChevronRight className={`h-3 w-3 transition-transform ${showHistory ? "rotate-90" : ""}`} />
            {row.history?.length || 0} history entr{(row.history?.length || 0) === 1 ? "y" : "ies"}
          </button>
          {showHistory && (
            <div className="mt-1.5 rounded border border-zinc-200 bg-zinc-50 p-2 text-2xs space-y-1"
                 data-testid={`canonical-history-${row.canonical_key}`}>
              {(row.history || []).map((h, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className="uppercase tracking-wider font-medium text-zinc-600 shrink-0 w-[60px]">
                    {h.action}
                  </span>
                  <span className="text-zinc-700 flex-1 min-w-0">
                    <span className="font-mono break-words">{displayValue(h.value)}</span>
                    {h.note && <div className="text-zinc-500 italic">"{h.note}"</div>}
                  </span>
                  <span className="text-zinc-400 shrink-0">
                    {h.at ? new Date(h.at).toLocaleString() : ""}
                    {h.actor && ` · ${h.actor}`}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button size="sm" variant="outline" onClick={() => onAct("verify")}
                  className="text-emerald-800 border-emerald-300 hover:bg-emerald-50"
                  data-testid={`canonical-verify-${row.canonical_key}`}>
            <Check className="h-3.5 w-3.5 mr-1" /> Verify
          </Button>
          <Button size="sm" variant="outline" onClick={() => onAct("amend")}
                  className="text-sky-800 border-sky-300 hover:bg-sky-50"
                  data-testid={`canonical-amend-${row.canonical_key}`}>
            <Pencil className="h-3.5 w-3.5 mr-1" /> Amend
          </Button>
          <Button size="sm" variant="outline" onClick={() => onAct("decline")}
                  className="text-rose-800 border-rose-300 hover:bg-rose-50"
                  data-testid={`canonical-decline-${row.canonical_key}`}>
            <X className="h-3.5 w-3.5 mr-1" /> Decline
          </Button>
          <Button size="sm" variant="outline" onClick={() => onAct("defer")}
                  data-testid={`canonical-defer-${row.canonical_key}`}>
            <Clock className="h-3.5 w-3.5 mr-1" /> Defer
          </Button>
        </div>
      </div>
    </div>
  );
}


function ActionDialog({ row, kind, email, onClose, onDone }) {
  const [value, setValue] = useState(
    typeof row.value === "string" ? row.value : (row.value ?? "").toString(),
  );
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const title = {
    verify: "Verify this field", amend: "Amend this field",
    decline: "Decline this field", defer: "Defer this field",
  }[kind];
  const canConfirm =
    kind === "amend" ? value !== "" : (kind === "verify" ? true : (note.trim().length >= 3));

  async function submit() {
    setSaving(true);
    try {
      const path = `/canonical/${encodeURIComponent(email)}/${row.canonical_key}/${kind}`;
      const body = kind === "amend"
        ? { value: coerce(value, row.value), note: note || null }
        : { note: note || null };
      await api.post(path, body);
      toast.success(`${title.split(" ")[0]}d ${row.canonical_key}`);
      onDone();
    } catch (e) {
      toast.error("Save failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid={`canonical-action-dialog-${kind}`}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div>
            <div className="text-xs font-medium text-zinc-600 mb-0.5">Canonical key</div>
            <code className="text-2xs font-mono">{row.canonical_key}</code>
          </div>
          <div>
            <div className="text-xs font-medium text-zinc-600 mb-0.5">
              {kind === "amend" ? "New value" : "Current value"}
            </div>
            {kind === "amend" ? (
              <Input value={value} onChange={(e) => setValue(e.target.value)}
                     data-testid="canonical-amend-value" autoFocus />
            ) : (
              <div className="text-sm px-2 py-1.5 rounded bg-zinc-50 border border-zinc-200 whitespace-pre-wrap break-words">
                {displayValue(row.value)}
              </div>
            )}
          </div>
          <div>
            <div className="text-xs font-medium text-zinc-600 mb-0.5">
              Note{kind === "verify" ? " (optional)" : ""}
              {(kind === "decline" || kind === "defer") && (
                <span className="text-rose-600 ml-0.5">*</span>
              )}
            </div>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)}
                       rows={2} data-testid="canonical-action-note"
                       placeholder={
                         kind === "verify" ? "e.g. confirmed with client on phone"
                         : kind === "amend" ? "why you're changing this value"
                         : kind === "decline" ? "why this value is inaccurate"
                         : "reason for deferring — will confirm at next appointment"
                       } />
            {(kind === "decline" || kind === "defer") && note.trim().length < 3 && (
              <div className="text-2xs text-rose-600 mt-0.5">
                A brief reason is required for {kind}.
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={saving || !canConfirm}
                  data-testid="canonical-action-confirm">
            {saving ? "Saving…" : `Confirm ${kind}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Best-effort coercion so amending a numeric field with "3" stays a
// number in the DB (matching the shape the intake form used).
function coerce(input, original) {
  if (typeof original === "number") {
    const n = Number(input);
    if (!Number.isNaN(n)) return n;
  }
  if (typeof original === "boolean") {
    if (["true", "yes", "1"].includes(String(input).toLowerCase())) return true;
    if (["false", "no", "0"].includes(String(input).toLowerCase())) return false;
  }
  return input;
}
